<template>
  <div id="pdf-viewer">
    <component :is="'script'" type="application/l10n">
      {{ defaultLocale }}
    </component>
    <div id="outerContainer" ref="outerContainerElement" :class="{ sidebarOpen: isSideBarOpen }">
      <div v-show="showElem('sidebar')" id="sidebarContainer">
        <div v-show="!isSidebarToolbarHidden" id="toolbarSidebar">
          <div class="splitToolbarButton toggled">
            <button
              v-show="showElem('sidebar.viewThumbnail')"
              id="viewThumbnail"
              class="toolbarButton toggled vue-pdf-app-icon view-thumbnail"
              title="Show Thumbnails"
              tabindex="2"
              data-l10n-id="thumbs"
            >
              <span data-l10n-id="thumbs_label">Thumbnails</span>
            </button>
            <button
              v-show="showElem('sidebar.viewOutline')"
              id="viewOutline"
              class="toolbarButton vue-pdf-app-icon view-outline"
              title="Show Document Outline (double-click to expand/collapse all items)"
              tabindex="3"
              data-l10n-id="document_outline"
            >
              <span data-l10n-id="document_outline_label">Document Outline</span>
            </button>
            <button
              v-show="showElem('sidebar.viewAttachments')"
              id="viewAttachments"
              class="toolbarButton vue-pdf-app-icon view-attachments"
              title="Show Attachments"
              tabindex="4"
              data-l10n-id="attachments"
            >
              <span data-l10n-id="attachments_label">Attachments</span>
            </button>
          </div>
        </div>
        <div
          v-show="showElem('sidebar')"
          id="sidebarContent"
          :class="{ 'zero-top': isSidebarToolbarHidden }"
        >
          <div v-show="showElem('sidebar.viewThumbnail')" id="thumbnailView"></div>
          <div v-show="showElem('sidebar.viewOutline')" id="outlineView" class="hidden"></div>
          <div
            v-show="showElem('sidebar.viewAttachments')"
            id="attachmentsView"
            class="hidden"
          ></div>
        </div>
        <div id="sidebarResizer" class="hidden"></div>
      </div>
      <!-- sidebarContainer -->

      <div id="mainContainer">
        <div
          v-show="showElem('toolbar.toolbarViewerLeft.findbar')"
          id="findbar"
          class="findbar doorHanger"
          :class="{ hidden: !isFindbarOpen }"
        >
          <div id="findbarInputContainer">
            <input
              id="findInput"
              v-model="findInput"
              class="toolbarField"
              title="Find"
              placeholder="Find in document…"
              tabindex="91"
              data-l10n-id="find_input"
              @keyup.enter="handleFindNext"
            />
            <div class="splitToolbarButton">
              <button
                id="findPrevious"
                class="toolbarButton findPrevious vue-pdf-app-icon find-previous"
                title="Find the previous occurrence of the phrase"
                tabindex="92"
                data-l10n-id="find_previous"
                @click="handleFindPrevious"
              >
                <span data-l10n-id="find_previous_label">Previous</span>
              </button>
              <div class="splitToolbarButtonSeparator"></div>
              <button
                id="findNext"
                class="toolbarButton findNext vue-pdf-app-icon find-next"
                title="Find the next occurrence of the phrase"
                tabindex="93"
                data-l10n-id="find_next"
                @click="handleFindNext"
              >
                <span data-l10n-id="find_next_label">Next</span>
              </button>
            </div>
          </div>

          <div id="findbarOptionsOneContainer">
            <input
              id="findHighlightAll"
              v-model="findHighlightAll"
              type="checkbox"
              class="toolbarField"
              tabindex="94"
              @change="handleFindOptionChange"
            />
            <label for="findHighlightAll" class="toolbarLabel" data-l10n-id="find_highlight"
              >Highlight all</label
            >
            <input
              id="findMatchCase"
              v-model="findMatchCase"
              type="checkbox"
              class="toolbarField"
              tabindex="95"
              @change="handleFindOptionChange"
            />
            <label for="findMatchCase" class="toolbarLabel" data-l10n-id="find_match_case_label"
              >Match case</label
            >
          </div>
          <div id="findbarOptionsTwoContainer">
            <input
              id="findEntireWord"
              v-model="findEntireWord"
              type="checkbox"
              class="toolbarField"
              tabindex="96"
              @change="handleFindOptionChange"
            />
            <label for="findEntireWord" class="toolbarLabel" data-l10n-id="find_entire_word_label"
              >Whole words</label
            >
            <span id="findResultsCount" class="toolbarLabel hidden"></span>
          </div>

          <div id="findbarMessageContainer">
            <span id="findMsg" class="toolbarLabel"></span>
          </div>
        </div>
        <!-- findbar -->

        <div
          v-show="showElem('secondaryToolbar')"
          id="secondaryToolbar"
          class="secondaryToolbar doorHangerRight"
          :class="{ hidden: !isSecondaryToolbarOpen }"
        >
          <div id="secondaryToolbarButtonContainer">
            <button
              v-show="showElem('secondaryToolbar.secondaryPresentationMode')"
              id="secondaryPresentationMode"
              class="secondaryToolbarButton presentationMode visibleLargeView vue-pdf-app-icon presentation-mode"
              title="Switch to Presentation Mode"
              tabindex="51"
              data-l10n-id="presentation_mode"
            >
              <span data-l10n-id="presentation_mode_label">Presentation Mode</span>
            </button>

            <button
              v-show="showElem('secondaryToolbar.secondaryOpenFile')"
              id="secondaryOpenFile"
              class="secondaryToolbarButton openFile visibleLargeView vue-pdf-app-icon open-file"
              title="Open File"
              tabindex="52"
              data-l10n-id="open_file"
            >
              <span data-l10n-id="open_file_label">Open</span>
            </button>

            <button
              v-show="showElem('secondaryToolbar.secondaryPrint')"
              id="secondaryPrint"
              class="secondaryToolbarButton print visibleMediumView vue-pdf-app-icon print-button"
              title="Print"
              tabindex="53"
              data-l10n-id="print"
            >
              <span data-l10n-id="print_label">Print</span>
            </button>

            <button
              v-show="showElem('secondaryToolbar.secondaryDownload')"
              id="secondaryDownload"
              class="secondaryToolbarButton download visibleMediumView vue-pdf-app-icon download-button"
              title="Download"
              tabindex="54"
              data-l10n-id="download"
            >
              <span data-l10n-id="download_label">Download</span>
            </button>

            <a
              v-show="showElem('secondaryToolbar.secondaryViewBookmark')"
              id="secondaryViewBookmark"
              href="#"
              class="secondaryToolbarButton bookmark visibleSmallView vue-pdf-app-icon bookmark-button"
              title="Current view (copy or open in new window)"
              tabindex="55"
              data-l10n-id="bookmark"
            >
              <span data-l10n-id="bookmark_label">Current View</span>
            </a>

            <div
              v-if="
                showElem('secondaryToolbar.secondaryPresentationMode') ||
                showElem('secondaryToolbar.secondaryOpenFile') ||
                showElem('secondaryToolbar.secondaryPrint') ||
                showElem('secondaryToolbar.secondaryDownload') ||
                showElem('secondaryToolbar.secondaryViewBookmark')
              "
              class="horizontalToolbarSeparator visibleLargeView"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.firstPage')"
              id="firstPage"
              class="secondaryToolbarButton firstPage vue-pdf-app-icon first-page"
              title="Go to First Page"
              tabindex="56"
              data-l10n-id="first_page"
              :disabled="isPreviousPageButtonDisabled"
              @click="handleGoToFirstPage"
            >
              <span data-l10n-id="first_page_label">Go to First Page</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.lastPage')"
              id="lastPage"
              class="secondaryToolbarButton lastPage vue-pdf-app-icon last-page"
              title="Go to Last Page"
              tabindex="57"
              data-l10n-id="last_page"
              :disabled="isNextPageButtonDisabled"
              @click="handleGoToLastPage"
            >
              <span data-l10n-id="last_page_label">Go to Last Page</span>
            </button>

            <div
              v-if="showElem('secondaryToolbar.lastPage') || showElem('secondaryToolbar.firstPage')"
              class="horizontalToolbarSeparator"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.pageRotateCw')"
              id="pageRotateCw"
              class="secondaryToolbarButton rotateCw vue-pdf-app-icon rotate-clockwise"
              title="Rotate Clockwise"
              tabindex="58"
              data-l10n-id="page_rotate_cw"
              @click="handleRotateClockwise"
            >
              <span data-l10n-id="page_rotate_cw_label">Rotate Clockwise</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.pageRotateCcw')"
              id="pageRotateCcw"
              class="secondaryToolbarButton rotateCcw vue-pdf-app-icon rotate-counter-clockwise"
              title="Rotate Counterclockwise"
              tabindex="59"
              data-l10n-id="page_rotate_ccw"
              @click="handleRotateCounterClockwise"
            >
              <span data-l10n-id="page_rotate_ccw_label">Rotate Counterclockwise</span>
            </button>

            <div
              v-if="
                showElem('secondaryToolbar.pageRotateCcw') ||
                showElem('secondaryToolbar.pageRotateCw')
              "
              class="horizontalToolbarSeparator"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.cursorSelectTool')"
              id="cursorSelectTool"
              class="secondaryToolbarButton selectTool toggled vue-pdf-app-icon select-tool"
              title="Enable Text Selection Tool"
              tabindex="60"
              data-l10n-id="cursor_text_select_tool"
            >
              <span data-l10n-id="cursor_text_select_tool_label">Text Selection Tool</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.cursorHandTool')"
              id="cursorHandTool"
              class="secondaryToolbarButton handTool vue-pdf-app-icon hand-tool"
              title="Enable Hand Tool"
              tabindex="61"
              data-l10n-id="cursor_hand_tool"
              @click="handleToggleHandTool"
            >
              <span data-l10n-id="cursor_hand_tool_label">Hand Tool</span>
            </button>

            <div
              v-if="
                showElem('secondaryToolbar.cursorHandTool') ||
                showElem('secondaryToolbar.cursorSelectTool')
              "
              class="horizontalToolbarSeparator"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.scrollVertical')"
              id="scrollVertical"
              class="secondaryToolbarButton scrollModeButtons scrollVertical toggled vue-pdf-app-icon scroll-vertical"
              title="Use Vertical Scrolling"
              tabindex="62"
              data-l10n-id="scroll_vertical"
            >
              <span data-l10n-id="scroll_vertical_label">Vertical Scrolling</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.scrollHorizontal')"
              id="scrollHorizontal"
              class="secondaryToolbarButton scrollModeButtons scrollHorizontal vue-pdf-app-icon scroll-horizontal"
              title="Use Horizontal Scrolling"
              tabindex="63"
              data-l10n-id="scroll_horizontal"
            >
              <span data-l10n-id="scroll_horizontal_label">Horizontal Scrolling</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.scrollWrapped')"
              id="scrollWrapped"
              class="secondaryToolbarButton scrollModeButtons scrollWrapped vue-pdf-app-icon scroll-wrapped"
              title="Use Wrapped Scrolling"
              tabindex="64"
              data-l10n-id="scroll_wrapped"
            >
              <span data-l10n-id="scroll_wrapped_label">Wrapped Scrolling</span>
            </button>

            <div
              v-if="
                showElem('secondaryToolbar.scrollWrapped') ||
                showElem('secondaryToolbar.scrollHorizontal') ||
                showElem('secondaryToolbar.scrollVertical')
              "
              class="horizontalToolbarSeparator scrollModeButtons"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.spreadNone')"
              id="spreadNone"
              class="secondaryToolbarButton spreadModeButtons spreadNone toggled vue-pdf-app-icon spread-none"
              title="Do not join page spreads"
              tabindex="65"
              data-l10n-id="spread_none"
            >
              <span data-l10n-id="spread_none_label">No Spreads</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.spreadOdd')"
              id="spreadOdd"
              class="secondaryToolbarButton spreadModeButtons vue-pdf-app-icon spread-odd"
              title="Join page spreads starting with odd-numbered pages"
              tabindex="66"
              data-l10n-id="spread_odd"
            >
              <span data-l10n-id="spread_odd_label">Odd Spreads</span>
            </button>
            <button
              v-show="showElem('secondaryToolbar.spreadEven')"
              id="spreadEven"
              class="secondaryToolbarButton spreadModeButtons spreadEven vue-pdf-app-icon spread-even"
              title="Join page spreads starting with even-numbered pages"
              tabindex="67"
              data-l10n-id="spread_even"
            >
              <span data-l10n-id="spread_even_label">Even Spreads</span>
            </button>

            <div
              v-if="
                showElem('secondaryToolbar.spreadEven') ||
                showElem('secondaryToolbar.spreadOdd') ||
                showElem('secondaryToolbar.spreadNone')
              "
              class="horizontalToolbarSeparator spreadModeButtons"
            ></div>

            <button
              v-show="showElem('secondaryToolbar.documentProperties')"
              id="documentProperties"
              class="secondaryToolbarButton documentProperties vue-pdf-app-icon document-properties"
              title="Document Properties…"
              tabindex="68"
              data-l10n-id="document_properties"
              @click="handleOpenDocumentPropertiesDialog"
            >
              <span data-l10n-id="document_properties_label">Document Properties…</span>
            </button>
          </div>
        </div>
        <!-- secondaryToolbar -->

        <div v-show="showElem('toolbar')" class="toolbar">
          <div id="toolbarContainer">
            <div id="toolbarViewer">
              <div id="toolbarViewerLeft">
                <button
                  v-show="showElem('sidebar')"
                  id="sidebarToggle"
                  class="toolbarButton vue-pdf-app-icon sidebar-toggle"
                  :class="{ toggled: isSideBarOpen }"
                  title="Toggle Sidebar"
                  tabindex="11"
                  data-l10n-id="toggle_sidebar"
                  @click="handleToggleSidebar"
                >
                  <span data-l10n-id="toggle_sidebar_label">Toggle Sidebar</span>
                </button>
                <div v-if="showElem('sidebar')" class="toolbarButtonSpacer"></div>
                <button
                  v-show="showElem('toolbar.toolbarViewerLeft.findbar')"
                  id="viewFind"
                  class="toolbarButton vue-pdf-app-icon view-find"
                  :class="{ toggled: isFindbarOpen }"
                  title="Find in Document"
                  tabindex="12"
                  data-l10n-id="findbar"
                  @click="handleToggleFindbar"
                >
                  <span data-l10n-id="findbar_label">Find</span>
                </button>
                <div class="splitToolbarButton hiddenSmallView">
                  <button
                    v-show="showElem('toolbar.toolbarViewerLeft.previous')"
                    id="previous"
                    class="toolbarButton pageUp vue-pdf-app-icon page-up"
                    title="Previous Page"
                    tabindex="13"
                    data-l10n-id="previous"
                    :disabled="isPreviousPageButtonDisabled"
                    @click="handlePreviousPage"
                  >
                    <span data-l10n-id="previous_label">Previous</span>
                  </button>
                  <div
                    v-if="
                      showElem('toolbar.toolbarViewerLeft.next') &&
                      showElem('toolbar.toolbarViewerLeft.previous')
                    "
                    class="splitToolbarButtonSeparator"
                  ></div>
                  <button
                    v-show="showElem('toolbar.toolbarViewerLeft.next')"
                    id="next"
                    class="toolbarButton pageDown vue-pdf-app-icon page-down"
                    title="Next Page"
                    tabindex="14"
                    data-l10n-id="next"
                    :disabled="isNextPageButtonDisabled"
                    @click="handleNextPage"
                  >
                    <span data-l10n-id="next_label">Next</span>
                  </button>
                </div>
                <input
                  v-show="showElem('toolbar.toolbarViewerLeft.pageNumber')"
                  id="pageNumber"
                  type="number"
                  class="toolbarField pageNumber"
                  title="Page"
                  :value="currentPageNumber"
                  size="4"
                  min="1"
                  tabindex="15"
                  data-l10n-id="page"
                  autocomplete="off"
                  @change="handlePageNumberChange"
                />
                <span
                  v-show="showElem('toolbar.toolbarViewerLeft.pageNumber')"
                  id="numPages"
                  class="toolbarLabel"
                ></span>
              </div>
              <div id="toolbarViewerRight">
                <slot name="toolbar-open-in-new-tab" />
                <slot name="toolbar-capture" />

                <button
                  v-show="showElem('toolbar.toolbarViewerRight.presentationMode')"
                  id="presentationMode"
                  class="toolbarButton presentationMode hiddenLargeView vue-pdf-app-icon presentation-mode"
                  title="Switch to Presentation Mode"
                  tabindex="31"
                  data-l10n-id="presentation_mode"
                  @click="handleSwitchToPresentationMode"
                >
                  <span data-l10n-id="presentation_mode_label">Presentation Mode</span>
                </button>

                <button
                  v-show="showElem('toolbar.toolbarViewerRight.openFile')"
                  id="openFile"
                  class="toolbarButton openFile hiddenLargeView vue-pdf-app-icon open-file"
                  title="Open File"
                  tabindex="32"
                  data-l10n-id="open_file"
                >
                  <span data-l10n-id="open_file_label">Open</span>
                </button>

                <button
                  v-show="showElem('toolbar.toolbarViewerRight.print')"
                  id="print"
                  class="toolbarButton print hiddenMediumView vue-pdf-app-icon print-button"
                  title="Print"
                  tabindex="33"
                  data-l10n-id="print"
                  :hidden="!(props.canPrint && props.accessible)"
                  @click="handlePrint"
                >
                  <span data-l10n-id="print_label">Print</span>
                </button>

                <button
                  v-show="showElem('toolbar.toolbarViewerRight.download')"
                  id="download"
                  class="toolbarButton download hiddenMediumView vue-pdf-app-icon download-button"
                  title="Download"
                  tabindex="34"
                  data-l10n-id="download"
                  :hidden="!(props.canDownload && props.accessible)"
                  @click="handleDownload"
                >
                  <span data-l10n-id="download_label">Download</span>
                </button>
                <a
                  v-show="showElem('toolbar.toolbarViewerRight.viewBookmark')"
                  id="viewBookmark"
                  href="#"
                  class="toolbarButton bookmark hiddenSmallView vue-pdf-app-icon bookmark-button"
                  title="Current view (copy or open in new window)"
                  tabindex="35"
                  data-l10n-id="bookmark"
                >
                  <span data-l10n-id="bookmark_label">Current View</span>
                </a>
                <div
                  v-if="showElem('secondaryToolbar')"
                  class="verticalToolbarSeparator hiddenSmallView"
                ></div>

                <button
                  v-show="showElem('secondaryToolbar')"
                  id="secondaryToolbarToggle"
                  class="toolbarButton vue-pdf-app-icon secondary-toolbar-toggle"
                  :class="{ toggled: isSecondaryToolbarOpen }"
                  title="Tools"
                  tabindex="36"
                  data-l10n-id="tools"
                  @click="handleToggleSecondaryToolbar"
                >
                  <span data-l10n-id="tools_label">Tools</span>
                </button>

                <slot name="toolbar-extra" />
              </div>
              <div id="toolbarViewerMiddle">
                <div class="splitToolbarButton">
                  <button
                    v-show="showElem('toolbar.toolbarViewerMiddle.zoomOut')"
                    id="zoomOut"
                    class="toolbarButton zoomOut vue-pdf-app-icon zoom-out"
                    title="Zoom Out"
                    tabindex="21"
                    data-l10n-id="zoom_out"
                    @click="handleZoomOut"
                  >
                    <span data-l10n-id="zoom_out_label">Zoom Out</span>
                  </button>
                  <div
                    v-if="
                      showElem('toolbar.toolbarViewerMiddle.zoomIn') &&
                      showElem('toolbar.toolbarViewerMiddle.zoomOut')
                    "
                    class="splitToolbarButtonSeparator"
                  ></div>
                  <button
                    v-show="showElem('toolbar.toolbarViewerMiddle.zoomIn')"
                    id="zoomIn"
                    class="toolbarButton zoomIn vue-pdf-app-icon zoom-in"
                    title="Zoom In"
                    tabindex="22"
                    data-l10n-id="zoom_in"
                    @click="handleZoomIn"
                  >
                    <span data-l10n-id="zoom_in_label">Zoom In</span>
                  </button>
                </div>
                <span
                  v-show="showElem('toolbar.toolbarViewerMiddle.scaleSelectContainer')"
                  id="scaleSelectContainer"
                  class="dropdownToolbarButton vue-pdf-app-icon dropdown-toolbar-button"
                >
                  <select
                    id="scaleSelect"
                    v-model="selectedScale"
                    title="Zoom"
                    tabindex="0"
                    data-l10n-id="pdfjs-zoom-select"
                    @change="handleScaleChange"
                  >
                    <option id="pageAutoOption" value="auto" data-l10n-id="pdfjs-page-scale-auto">
                      Automatic Zoom
                    </option>
                    <option
                      id="pageActualOption"
                      value="page-actual"
                      data-l10n-id="pdfjs-page-scale-actual"
                    >
                      Actual Size
                    </option>
                    <option id="pageFitOption" value="page-fit" data-l10n-id="pdfjs-page-scale-fit">
                      Page Fit
                    </option>
                    <option
                      id="pageWidthOption"
                      value="page-width"
                      data-l10n-id="pdfjs-page-scale-width"
                    >
                      Page Width
                    </option>
                    <option
                      id="customScaleOption"
                      value="custom"
                      disabled
                      hidden
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 0 }'
                    >
                      0%
                    </option>
                    <option
                      value="0.5"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 50 }'
                    >
                      50%
                    </option>
                    <option
                      value="0.75"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 75 }'
                    >
                      75%
                    </option>
                    <option
                      value="1"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 100 }'
                    >
                      100%
                    </option>
                    <option
                      value="1.25"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 125 }'
                    >
                      125%
                    </option>
                    <option
                      value="1.5"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 150 }'
                    >
                      150%
                    </option>
                    <option
                      value="2"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 200 }'
                    >
                      200%
                    </option>
                    <option
                      value="3"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 300 }'
                    >
                      300%
                    </option>
                    <option
                      value="4"
                      data-l10n-id="pdfjs-page-scale-percent"
                      data-l10n-args='{ "scale": 400 }'
                    >
                      400%
                    </option>
                  </select>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div id="loadingBar">
          <div class="progress">
            <div class="glimmer"></div>
          </div>
        </div>
        <menu v-show="false" id="viewerContextMenu" type="context">
          <menuitem id="contextFirstPage" label="First Page" data-l10n-id="first_page" />
          <menuitem id="contextLastPage" label="Last Page" data-l10n-id="last_page" />
          <menuitem
            id="contextPageRotateCw"
            label="Rotate Clockwise"
            data-l10n-id="page_rotate_cw"
          />
          <menuitem
            id="contextPageRotateCcw"
            label="Rotate Counter-Clockwise"
            data-l10n-id="page_rotate_ccw"
          />
        </menu>
        <div id="viewerContainer" tabindex="0">
          <div id="viewer" class="pdfViewer"></div>
        </div>
        <div v-show="showElem('errorWrapper')" id="errorWrapper" hidden="true">
          <div id="errorMessageLeft">
            <span id="errorMessage"></span>
            <button id="errorShowMore" data-l10n-id="error_more_info">More Information</button>
            <button id="errorShowLess" data-l10n-id="error_less_info" hidden="true">
              Less Information
            </button>
          </div>
          <div id="errorMessageRight">
            <button id="errorClose" data-l10n-id="error_close">Close</button>
          </div>
          <div class="clearBoth"></div>
          <textarea id="errorMoreInfo" hidden="true" readonly></textarea>
        </div>
      </div>
      <!-- mainContainer -->

      <div id="dialogContainer">
        <dialog id="passwordDialog">
          <div class="row">
            <label id="passwordText" for="password" data-l10n-id="pdfjs-password-label"
              >Enter the password to open this PDF file:</label
            >
          </div>
          <div class="row">
            <input id="password" type="password" class="toolbarField" />
          </div>
          <div class="buttonRow">
            <button id="passwordCancel" class="dialogButton" type="button">
              <span data-l10n-id="pdfjs-password-cancel-button">Cancel</span>
            </button>
            <button id="passwordSubmit" class="dialogButton" type="button">
              <span data-l10n-id="pdfjs-password-ok-button">OK</span>
            </button>
          </div>
        </dialog>
        <dialog id="documentPropertiesDialog" ref="documentPropertiesDialogElement">
          <div class="row">
            <span id="fileNameLabel" data-l10n-id="pdfjs-document-properties-file-name"
              >File name:</span
            >
            <p id="fileNameField" aria-labelledby="fileNameLabel">-</p>
          </div>
          <div class="row">
            <span id="fileSizeLabel" data-l10n-id="pdfjs-document-properties-file-size"
              >File size:</span
            >
            <p id="fileSizeField" aria-labelledby="fileSizeLabel">-</p>
          </div>
          <div class="separator"></div>
          <div class="row">
            <span id="titleLabel" data-l10n-id="pdfjs-document-properties-title">Title:</span>
            <p id="titleField" aria-labelledby="titleLabel">-</p>
          </div>
          <div class="row">
            <span id="authorLabel" data-l10n-id="pdfjs-document-properties-author">Author:</span>
            <p id="authorField" aria-labelledby="authorLabel">-</p>
          </div>
          <div class="row">
            <span id="subjectLabel" data-l10n-id="pdfjs-document-properties-subject">Subject:</span>
            <p id="subjectField" aria-labelledby="subjectLabel">-</p>
          </div>
          <div class="row">
            <span id="keywordsLabel" data-l10n-id="pdfjs-document-properties-keywords"
              >Keywords:</span
            >
            <p id="keywordsField" aria-labelledby="keywordsLabel">-</p>
          </div>
          <div class="row">
            <span id="creationDateLabel" data-l10n-id="pdfjs-document-properties-creation-date"
              >Creation Date:</span
            >
            <p id="creationDateField" aria-labelledby="creationDateLabel">-</p>
          </div>
          <div class="row">
            <span
              id="modificationDateLabel"
              data-l10n-id="pdfjs-document-properties-modification-date"
              >Modification Date:</span
            >
            <p id="modificationDateField" aria-labelledby="modificationDateLabel">-</p>
          </div>
          <div class="row">
            <span id="creatorLabel" data-l10n-id="pdfjs-document-properties-creator">Creator:</span>
            <p id="creatorField" aria-labelledby="creatorLabel">-</p>
          </div>
          <div class="separator"></div>
          <div class="row">
            <span id="producerLabel" data-l10n-id="pdfjs-document-properties-producer"
              >PDF Producer:</span
            >
            <p id="producerField" aria-labelledby="producerLabel">-</p>
          </div>
          <div class="row">
            <span id="versionLabel" data-l10n-id="pdfjs-document-properties-version"
              >PDF Version:</span
            >
            <p id="versionField" aria-labelledby="versionLabel">-</p>
          </div>
          <div class="row">
            <span id="pageCountLabel" data-l10n-id="pdfjs-document-properties-page-count"
              >Page Count:</span
            >
            <p id="pageCountField" aria-labelledby="pageCountLabel">-</p>
          </div>
          <div class="row">
            <span id="pageSizeLabel" data-l10n-id="pdfjs-document-properties-page-size"
              >Page Size:</span
            >
            <p id="pageSizeField" aria-labelledby="pageSizeLabel">-</p>
          </div>
          <div class="separator"></div>
          <div class="row">
            <span id="linearizedLabel" data-l10n-id="pdfjs-document-properties-linearized"
              >Fast Web View:</span
            >
            <p id="linearizedField" aria-labelledby="linearizedLabel">-</p>
          </div>
          <div class="buttonRow">
            <button
              id="documentPropertiesClose"
              class="dialogButton"
              type="button"
              @click="handleCloseDocumentPropertiesDialog"
            >
              <span data-l10n-id="pdfjs-document-properties-close-button">Close</span>
            </button>
          </div>
        </dialog>
        <dialog
          id="altTextDialog"
          class="dialog altText"
          aria-labelledby="dialogLabel"
          aria-describedby="dialogDescription"
        >
          <div id="altTextContainer" class="mainContainer">
            <div id="overallDescription">
              <span id="dialogLabel" data-l10n-id="pdfjs-editor-alt-text-dialog-label" class="title"
                >Choose an option</span
              >
              <span id="dialogDescription" data-l10n-id="pdfjs-editor-alt-text-dialog-description">
                Alt text (alternative text) helps when people can’t see the image or when it doesn’t
                load.
              </span>
            </div>
            <div id="addDescription">
              <div class="radio">
                <div class="radioButton">
                  <input
                    id="descriptionButton"
                    type="radio"
                    name="altTextOption"
                    tabindex="0"
                    aria-describedby="descriptionAreaLabel"
                    checked
                  />
                  <label
                    for="descriptionButton"
                    data-l10n-id="pdfjs-editor-alt-text-add-description-label"
                    >Add a description</label
                  >
                </div>
                <div class="radioLabel">
                  <span
                    id="descriptionAreaLabel"
                    data-l10n-id="pdfjs-editor-alt-text-add-description-description"
                  >
                    @/plugins/pdf-viewer/pdf.viewer.localehe subject, setting, or actions.
                  </span>
                </div>
              </div>
              <div class="descriptionArea">
                <textarea
                  id="descriptionTextarea"
                  placeholder="For example, “A young man sits down at a table to eat a meal”"
                  aria-labelledby="descriptionAreaLabel"
                  data-l10n-id="pdfjs-editor-alt-text-textarea"
                  tabindex="0"
                ></textarea>
              </div>
            </div>
            <div id="markAsDecorative">
              <div class="radio">
                <div class="radioButton">
                  <input
                    id="decorativeButton"
                    type="radio"
                    name="altTextOption"
                    aria-describedby="decorativeLabel"
                  />
                  <label
                    for="decorativeButton"
                    data-l10n-id="pdfjs-editor-alt-text-mark-decorative-label"
                    >Mark as decorative</label
                  >
                </div>
                <div class="radioLabel">
                  <span
                    id="decorativeLabel"
                    data-l10n-id="pdfjs-editor-alt-text-mark-decorative-description"
                  >
                    This is used for ornamental images, like borders or watermarks.
                  </span>
                </div>
              </div>
            </div>
            <div id="buttons">
              <button id="altTextCancel" class="secondaryButton" type="button" tabindex="0">
                <span data-l10n-id="pdfjs-editor-alt-text-cancel-button">Cancel</span>
              </button>
              <button id="altTextSave" class="primaryButton" type="button" tabindex="0">
                <span data-l10n-id="pdfjs-editor-alt-text-save-button">Save</span>
              </button>
            </div>
          </div>
        </dialog>
        <dialog
          id="newAltTextDialog"
          class="dialog newAltText"
          aria-labelledby="newAltTextTitle"
          aria-describedby="newAltTextDescription"
          tabindex="0"
        >
          <div id="newAltTextContainer" class="mainContainer">
            <div class="title">
              <span
                id="newAltTextTitle"
                data-l10n-id="pdfjs-editor-new-alt-text-dialog-edit-label"
                role="sectionhead"
                tabindex="0"
                >Edit alt text (image description)</span
              >
            </div>
            <div id="mainContent">
              <div id="descriptionAndSettings">
                <div id="descriptionInstruction">
                  <div id="newAltTextDescriptionContainer">
                    <div class="altTextSpinner" role="status" aria-live="polite"></div>
                    <textarea
                      id="newAltTextDescriptionTextarea"
                      placeholder="Write your description here…"
                      aria-labelledby="descriptionAreaLabel"
                      data-l10n-id="pdfjs-editor-new-alt-text-textarea"
                      tabindex="0"
                    ></textarea>
                  </div>
                  <span
                    id="newAltTextDescription"
                    role="note"
                    data-l10n-id="pdfjs-editor-new-alt-text-description"
                    >Short description for people who can’t see the image or when the image doesn’t
                    load.</span
                  >
                  <div id="newAltTextDisclaimer" role="note">
                    <div>
                      <span data-l10n-id="pdfjs-editor-new-alt-text-disclaimer1"
                        >This alt text was created automatically and may be inaccurate.</span
                      >
                      <a
                        id="newAltTextLearnMore"
                        href="https://support.mozilla.org/en-US/kb/pdf-alt-text"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-l10n-id="pdfjs-editor-new-alt-text-disclaimer-learn-more-url"
                        tabindex="0"
                        >Learn more</a
                      >
                    </div>
                  </div>
                </div>
                <div id="newAltTextCreateAutomatically" class="toggler">
                  <button
                    id="newAltTextCreateAutomaticallyButton"
                    class="toggle-button"
                    type="button"
                    aria-pressed="true"
                    tabindex="0"
                  ></button>
                  <label
                    for="newAltTextCreateAutomaticallyButton"
                    class="togglerLabel"
                    data-l10n-id="pdfjs-editor-new-alt-text-create-automatically-button-label"
                    >Create alt text automatically</label
                  >
                </div>
                <div id="newAltTextDownloadModel" class="hidden">
                  <span
                    id="newAltTextDownloadModelDescription"
                    data-l10n-id="pdfjs-editor-new-alt-text-ai-model-downloading-progress"
                    aria-valuemin="0"
                    data-l10n-args='{ "totalSize": 0, "downloadedSize": 0 }'
                    >Downloading alt text AI model (0 of 0 MB)</span
                  >
                </div>
              </div>
              <div id="newAltTextImagePreview"></div>
            </div>
            <div id="newAltTextError" class="messageBar">
              <div>
                <div>
                  <span class="title" data-l10n-id="pdfjs-editor-new-alt-text-error-title"
                    >Couldn’t create alt text automatically</span
                  >
                  <span
                    class="description"
                    data-l10n-id="pdfjs-editor-new-alt-text-error-description"
                    >Please write your own alt text or try again later.</span
                  >
                </div>
                <button
                  id="newAltTextCloseButton"
                  class="closeButton"
                  type="button"
                  tabindex="0"
                  title="Close"
                >
                  <span data-l10n-id="pdfjs-editor-new-alt-text-error-close-button">Close</span>
                </button>
              </div>
            </div>
            <div id="newAltTextButtons" class="dialogButtonsGroup">
              <button
                id="newAltTextCancel"
                type="button"
                class="secondaryButton hidden"
                tabindex="0"
              >
                <span data-l10n-id="pdfjs-editor-alt-text-cancel-button">Cancel</span>
              </button>
              <button id="newAltTextNotNow" type="button" class="secondaryButton" tabindex="0">
                <span data-l10n-id="pdfjs-editor-new-alt-text-not-now-button">Not now</span>
              </button>
              <button id="newAltTextSave" type="button" class="primaryButton" tabindex="0">
                <span data-l10n-id="pdfjs-editor-alt-text-save-button">Save</span>
              </button>
            </div>
          </div>
        </dialog>

        <dialog id="altTextSettingsDialog" class="dialog" aria-labelledby="altTextSettingsTitle">
          <div id="altTextSettingsContainer" class="mainContainer">
            <div class="title">
              <span
                id="altTextSettingsTitle"
                data-l10n-id="pdfjs-editor-alt-text-settings-dialog-label"
                role="sectionhead"
                tabindex="0"
                class="title"
                >Image alt text settings</span
              >
            </div>
            <div id="automaticAltText">
              <span data-l10n-id="pdfjs-editor-alt-text-settings-automatic-title"
                >Automatic alt text</span
              >
              <div id="automaticSettings">
                <div id="createModelSetting">
                  <div class="toggler">
                    <button
                      id="createModelButton"
                      type="button"
                      class="toggle-button"
                      aria-pressed="true"
                      tabindex="0"
                    ></button>
                    <label
                      for="createModelButton"
                      class="togglerLabel"
                      data-l10n-id="pdfjs-editor-alt-text-settings-create-model-button-label"
                      >Create alt text automatically</label
                    >
                  </div>
                  <div id="createModelDescription" class="description">
                    <span data-l10n-id="pdfjs-editor-alt-text-settings-create-model-description"
                      >Suggests descriptions to help people who can’t see the image or when the
                      image doesn’t load.</span
                    >
                    <a
                      id="altTextSettingsLearnMore"
                      href="https://support.mozilla.org/en-US/kb/pdf-alt-text"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-l10n-id="pdfjs-editor-new-alt-text-disclaimer-learn-more-url"
                      tabindex="0"
                      >Learn more</a
                    >
                  </div>
                </div>
                <div id="aiModelSettings">
                  <div>
                    <span
                      data-l10n-id="pdfjs-editor-alt-text-settings-download-model-label"
                      data-l10n-args='{ "totalSize": 180 }'
                      >Alt text AI model (180MB)</span
                    >
                    <div id="aiModelDescription" class="description">
                      <span data-l10n-id="pdfjs-editor-alt-text-settings-ai-model-description"
                        >Runs locally on your device so your data stays private. Required for
                        automatic alt text.</span
                      >
                    </div>
                  </div>
                  <button id="deleteModelButton" type="button" class="secondaryButton" tabindex="0">
                    <span data-l10n-id="pdfjs-editor-alt-text-settings-delete-model-button"
                      >Delete</span
                    >
                  </button>
                  <button
                    id="downloadModelButton"
                    type="button"
                    class="secondaryButton"
                    tabindex="0"
                  >
                    <span data-l10n-id="pdfjs-editor-alt-text-settings-download-model-button"
                      >Download</span
                    >
                  </button>
                </div>
              </div>
            </div>
            <div class="dialogSeparator"></div>
            <div id="altTextEditor">
              <span data-l10n-id="pdfjs-editor-alt-text-settings-editor-title"
                >Alt text editor</span
              >
              <div id="showAltTextEditor">
                <div class="toggler">
                  <button
                    id="showAltTextDialogButton"
                    type="button"
                    class="toggle-button"
                    aria-pressed="true"
                    tabindex="0"
                  ></button>
                  <label
                    for="showAltTextDialogButton"
                    class="togglerLabel"
                    data-l10n-id="pdfjs-editor-alt-text-settings-show-dialog-button-label"
                    >Show alt text editor right away when adding an image</label
                  >
                </div>
                <div id="showAltTextDialogDescription" class="description">
                  <span data-l10n-id="pdfjs-editor-alt-text-settings-show-dialog-description"
                    >Helps you make sure all your images have alt text.</span
                  >
                </div>
              </div>
            </div>
            <div id="buttons" class="dialogButtonsGroup">
              <button
                id="altTextSettingsCloseButton"
                type="button"
                class="primaryButton"
                tabindex="0"
              >
                <span data-l10n-id="pdfjs-editor-alt-text-settings-close-button">Close</span>
              </button>
            </div>
          </div>
        </dialog>
        <dialog id="printServiceDialog" style="min-width: 200px">
          <div class="row">
            <span data-l10n-id="pdfjs-print-progress-message"
              >Preparing document for printing…</span
            >
          </div>
          <div class="row">
            <progress value="0" max="100"></progress>
            <span
              data-l10n-id="pdfjs-print-progress-percent"
              data-l10n-args='{ "progress": 0 }'
              class="relative-progress"
              >0%</span
            >
          </div>
          <div class="buttonRow">
            <button id="printCancel" class="dialogButton" type="button">
              <span data-l10n-id="pdfjs-print-progress-close-button">Cancel</span>
            </button>
          </div>
        </dialog>
      </div>
      <!-- dialogContainer -->

      <div
        id="editorUndoBar"
        class="messageBar"
        role="status"
        aria-labelledby="editorUndoBarMessage"
        tabindex="-1"
        hidden
      >
        <div>
          <div>
            <span id="editorUndoBarMessage" class="description"></span>
          </div>
          <button
            id="editorUndoBarUndoButton"
            class="undoButton"
            type="button"
            tabindex="0"
            title="Undo"
            data-l10n-id="pdfjs-editor-undo-bar-undo-button"
          >
            <span data-l10n-id="pdfjs-editor-undo-bar-undo-button-label">Undo</span>
          </button>
          <button
            id="editorUndoBarCloseButton"
            class="closeButton"
            type="button"
            tabindex="0"
            title="Close"
            data-l10n-id="pdfjs-editor-undo-bar-close-button"
          >
            <span data-l10n-id="pdfjs-editor-undo-bar-close-button-label">Close</span>
          </button>
        </div>
      </div>
      <!-- editorUndoBar -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import * as pdfjsLib from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.mjs?worker&inline";
import {
  EventBus,
  PDFLinkService,
  PDFViewer,
  PDFFindController,
  DownloadManager,
} from "pdfjs-dist/web/pdf_viewer";
import pdfJsViewerCss from "pdfjs-dist/web/pdf_viewer.css?inline";
import pdfViewerCss from "@plugins/pdf-viewer/web/viewer.scss?inline";
import locale from "@plugins/pdf-viewer/pdf.viewer.locale";
import { toolbarConfig } from "@plugins/pdf-viewer/pdf.viewer.toolbar.config";

// workerPort is set fresh per loadPdf() call — see loadPdf()

const props = withDefaults(
  defineProps<{
    pdf?: string;
    accessible?: boolean;
    canDownload?: boolean;
    canPrint?: boolean;
  }>(),
  { pdf: "", accessible: true, canDownload: false, canPrint: false },
);

const emit = defineEmits<{
  (e: "open", payload: { pdfViewer: PDFViewer; eventBus: InstanceType<typeof EventBus> }): void;
  (e: "pages-rendered"): void;
}>();

let pdfViewer = null as PDFViewer | null;
let pdfDocument = null as pdfjsLib.PDFDocumentProxy | null;
const isSideBarOpen = ref(false);
const isFindbarOpen = ref(false);
const findInput = ref("");
const findHighlightAll = ref(false);
const findMatchCase = ref(false);
const findEntireWord = ref(false);
const isPreviousPageButtonDisabled = ref(true);
const isNextPageButtonDisabled = ref(false);
const currentPageNumber = ref(1);
const selectedScale = ref("auto");
const isSecondaryToolbarOpen = ref(false);
const downloadManager = new DownloadManager();
const isDocumentPropertiesDialogOpen = ref(false);
let documentFilename = "";

const pdfJsViewerStyleElement = ref<HTMLStyleElement | null>(null);
const pdfViewerStyleElement = ref<HTMLStyleElement | null>(null);

const documentPropertiesDialogElement = ref<HTMLDialogElement | null>(null);
const outerContainerElement = ref<HTMLElement | null>(null);

const showElem = (path) => {
  const parts = path.split(".");
  let node: unknown = (toolbarConfig as unknown as Record<string, unknown>)[parts[0]];
  for (let i = 1; i < parts.length; i++) {
    if (node && typeof node === "object") {
      node = (node as Record<string, unknown>)[parts[i]];
    } else {
      return false;
    }
  }
  return Boolean(node);
};

const isSidebarToolbarHidden = computed(() => {
  const sidebar = toolbarConfig.sidebar;
  return !(sidebar.viewAttachments && sidebar.viewOutline && sidebar.viewThumbnail);
});

const defaultLocale = computed(() => JSON.stringify(locale));

async function renderThumbnail(pageNumber: number) {
  const page = await pdfDocument?.getPage(pageNumber);
  const rotation = pdfViewer.pagesRotation;
  const scale = rotation === 90 || rotation === 270 ? 0.12 : 0.17;
  const viewport = page.getViewport({ scale, rotation });

  const container = document.getElementById("thumbnailView");
  if (!container) return;

  const wrapper = document.createElement("div");
  wrapper.className = "thumbnail";
  wrapper.setAttribute("data-loaded", "true");

  const canvas = document.createElement("canvas");
  canvas.className = "thumbnailImage";
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  canvas.setAttribute("aria-label", `Page ${pageNumber}`);

  wrapper.appendChild(canvas);
  container.appendChild(wrapper);

  // Navigate to clicked page
  wrapper.addEventListener("click", () => {
    currentPageNumber.value = pageNumber;
    pdfViewer.currentPageNumber = pageNumber;
  });

  const canvasContext = canvas.getContext("2d");
  if (!canvasContext) return;

  await page.render({ canvasContext: canvasContext, canvas, viewport }).promise;
}

async function renderAllThumbnails() {
  const container = document.getElementById("thumbnailView");
  if (!container) return;
  container.innerHTML = "";

  for (let i = 1; i <= pdfViewer.pagesCount; i++) {
    await renderThumbnail(i);
  }
}

function handleToggleSidebar() {
  isSideBarOpen.value = !isSideBarOpen.value;
}

function handleToggleFindbar() {
  isFindbarOpen.value = !isFindbarOpen.value;
}

function handleFind() {
  if (!pdfViewer) return;

  pdfViewer.eventBus.dispatch("find", {
    type: "",
    query: findInput.value,
    caseSensitive: findMatchCase.value,
    entireWord: findEntireWord.value,
    highlightAll: findHighlightAll.value,
  });
}

function handleFindNext() {
  if (!pdfViewer) return;

  pdfViewer.eventBus.dispatch("find", {
    type: "again",
    query: findInput.value,
    caseSensitive: findMatchCase.value,
    entireWord: findEntireWord.value,
    highlightAll: findHighlightAll.value,
    findPrevious: false,
  });
}

function handleFindPrevious() {
  if (!pdfViewer) return;

  pdfViewer.eventBus.dispatch("find", {
    type: "again",
    query: findInput.value,
    caseSensitive: findMatchCase.value,
    entireWord: findEntireWord.value,
    highlightAll: findHighlightAll.value,
    findPrevious: true,
  });
}

function handleFindOptionChange() {
  handleFind();
}

function handleNextPage() {
  if (pdfViewer && pdfViewer.currentPageNumber < pdfViewer.pagesCount) {
    pdfViewer.currentPageNumber++;
  }
}

function handlePreviousPage() {
  if (pdfViewer && pdfViewer.currentPageNumber > 1) {
    pdfViewer.currentPageNumber--;
  }
}

function handlePageNumberChange(event) {
  const num = parseInt(event.target.value, 10);
  if (pdfViewer && num >= 1 && num <= pdfViewer.pagesCount) {
    pdfViewer.currentPageNumber = num;
  }
}

function handleZoomOut() {
  if (pdfViewer) pdfViewer.decreaseScale();
  updateCustomScale();
}

function handleZoomIn() {
  if (pdfViewer) pdfViewer.increaseScale();
  updateCustomScale();
}

function updateCustomScale() {
  const listOfScale = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
  const currentScale = pdfViewer.currentScale;

  if (listOfScale.includes(currentScale)) {
    selectedScale.value = currentScale;
  } else {
    selectedScale.value = "custom";
    const option = document.getElementById("customScaleOption");
    if (option === null) return;
    option.setAttribute(
      "data-l10n-args",
      JSON.stringify({ scale: Math.trunc(currentScale * 100) }),
    );
    option.textContent = ` ${Math.trunc(currentScale * 100)}% `;
  }
}

function handleScaleChange(event) {
  if (pdfViewer) pdfViewer.currentScaleValue = event.target.value;
}

const isPresentationMode = ref(false);

function handleSwitchToPresentationMode() {
  const viewerContainer = document.getElementById("viewerContainer");
  if (!viewerContainer || !pdfViewer) return;

  const previousScale = pdfViewer.currentScaleValue;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isPresentationMode.value) return;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
      case "PageDown":
      case " ":
        e.preventDefault();
        handleNextPage();
        break;
      case "ArrowLeft":
      case "ArrowUp":
      case "PageUp":
        e.preventDefault();
        handlePreviousPage();
        break;
      case "Home":
        e.preventDefault();
        if (pdfViewer) pdfViewer.currentPageNumber = 1;
        break;
      case "End":
        e.preventDefault();
        if (pdfViewer) pdfViewer.currentPageNumber = pdfViewer.pagesCount;
        break;
    }
  };

  // Prevent mouse wheel free-scrolling — only allow full page jumps
  const handleWheel = (e: WheelEvent) => {
    if (!isPresentationMode.value) return;
    e.preventDefault();
    if (e.deltaY > 0) {
      handleNextPage();
    } else if (e.deltaY < 0) {
      handlePreviousPage();
    }
  };

  const handleFullscreenChange = () => {
    if (document.fullscreenElement === viewerContainer) {
      isPresentationMode.value = true;
      viewerContainer.classList.add("pdfPresentationMode");

      // Override overflow:hidden so scrollPageIntoView works
      viewerContainer.style.overflow = "scroll";
      // Hide scrollbar but keep scrolling functional
      viewerContainer.style.scrollbarWidth = "none";

      // Wait for fullscreen layout to settle before applying page-fit
      const initialWidth = viewerContainer.clientWidth;
      const initialHeight = viewerContainer.clientHeight;
      const ro = new ResizeObserver(() => {
        // Skip until dimensions actually change from fullscreen
        if (
          viewerContainer.clientWidth === initialWidth &&
          viewerContainer.clientHeight === initialHeight
        ) {
          return;
        }
        ro.disconnect();
        pdfViewer!.presentationModeState = 3;
        pdfViewer!.currentScaleValue = "page-fit";
        requestAnimationFrame(() => {
          pdfViewer!.scrollPageIntoView({ pageNumber: pdfViewer!.currentPageNumber });
        });
      });
      ro.observe(viewerContainer);

      document.addEventListener("keydown", handleKeyDown);
      viewerContainer.addEventListener("wheel", handleWheel, { passive: false });
    } else {
      isPresentationMode.value = false;
      pdfViewer!.presentationModeState = 1; // PresentationModeState.NORMAL
      viewerContainer.classList.remove("pdfPresentationMode");
      viewerContainer.style.overflow = "";
      viewerContainer.style.scrollbarWidth = "";

      pdfViewer!.currentScaleValue = previousScale;

      document.removeEventListener("keydown", handleKeyDown);
      viewerContainer.removeEventListener("wheel", handleWheel);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }
  };

  document.addEventListener("fullscreenchange", handleFullscreenChange);
  viewerContainer.requestFullscreen();
}

async function handlePrint() {
  if (!pdfDocument || !pdfViewer) return;

  const PRINT_RESOLUTION = 150;
  const PRINT_UNITS = PRINT_RESOLUTION / 72;
  const pagesCount = pdfDocument.numPages;

  // Render all pages in the main document context (where pdf.js fonts are loaded)
  const pageImages: string[] = [];
  const scratchCanvas = document.createElement("canvas");

  for (let i = 1; i <= pagesCount; i++) {
    const page = await pdfDocument.getPage(i);
    const viewport = page.getViewport({ scale: 1 });

    scratchCanvas.width = Math.floor(viewport.width * PRINT_UNITS);
    scratchCanvas.height = Math.floor(viewport.height * PRINT_UNITS);

    const ctx = scratchCanvas.getContext("2d")!;
    ctx.save();
    ctx.fillStyle = "rgb(255, 255, 255)";
    ctx.fillRect(0, 0, scratchCanvas.width, scratchCanvas.height);
    ctx.restore();

    await page.render({
      canvasContext: ctx,
      canvas: scratchCanvas,
      transform: [PRINT_UNITS, 0, 0, PRINT_UNITS, 0, 0],
      viewport,
      intent: "print",
    }).promise;

    pageImages.push(scratchCanvas.toDataURL("image/png"));
  }

  scratchCanvas.width = 0;
  scratchCanvas.height = 0;

  // Create a hidden iframe
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.left = "-9999px";
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument!;

  // Set title
  iframeDoc.title = documentFilename || "Print PDF";

  // Add print styles via DOM
  const style = iframeDoc.createElement("style");
  style.textContent = `
    @page { margin: 0; }
    body { margin: 0; padding: 0; }
    img {
      width: 100%;
      display: block;
      page-break-after: always;
      page-break-inside: avoid;
    }
    img:last-child {
      page-break-after: auto;
    }
  `;
  iframeDoc.head.appendChild(style);

  // Append images via DOM
  const imageLoadPromises: Promise<void>[] = [];

  for (const src of pageImages) {
    const img = iframeDoc.createElement("img");
    img.src = src;
    imageLoadPromises.push(
      new Promise<void>((resolve) => {
        if (img.complete) resolve();
        else img.onload = () => resolve();
      }),
    );
    iframeDoc.body.appendChild(img);
  }

  // Wait for all images to load, then print
  await Promise.all(imageLoadPromises);

  iframe.contentWindow!.focus();
  iframe.contentWindow!.print();

  iframe.contentWindow!.addEventListener("afterprint", () => {
    document.body.removeChild(iframe);
  });
}

async function handleDownload() {
  const data = await pdfDocument?.getData();
  const baseUrl = props.pdf.split("#", 1)[0];
  downloadManager.download(data, baseUrl, documentFilename);
}

function handleToggleSecondaryToolbar() {
  isSecondaryToolbarOpen.value = !isSecondaryToolbarOpen.value;
}

function closeSecondaryToolbar(event) {
  if (!isSecondaryToolbarOpen.value) {
    return;
  }
  const secondaryToolbarToggle = document.getElementById("secondaryToolbarToggle");
  const pageRotateCw = document.getElementById("pageRotateCw");
  const pageRotateCcw = document.getElementById("pageRotateCcw");

  if (
    !secondaryToolbarToggle?.contains(event.target as Node) &&
    !pageRotateCw?.contains(event.target as Node) &&
    !pageRotateCcw?.contains(event.target as Node)
  ) {
    isSecondaryToolbarOpen.value = false;
  }
}

function handleGoToFirstPage() {
  if (pdfViewer) pdfViewer.currentPageNumber = 1;
}

function handleGoToLastPage() {
  if (pdfViewer) pdfViewer.currentPageNumber = pdfViewer.pagesCount;
}

function handleRotateClockwise() {
  if (pdfViewer) pdfViewer.pagesRotation += 90;
  renderAllThumbnails();
}

function handleRotateCounterClockwise() {
  if (pdfViewer) pdfViewer.pagesRotation -= 90;
  renderAllThumbnails();
}

const isHandToolActive = ref(false);

function handleToggleHandTool() {
  isHandToolActive.value = true;
  activateHandTool();
}

function activateHandTool() {
  const viewer = document.getElementById("viewerContainer");
  if (!viewer) return;

  viewer.classList.add("grab-to-pan-grab");

  let startX = 0;
  let startY = 0;
  let scrollLeft = 0;
  let scrollTop = 0;
  let overlay: HTMLDivElement | null = null;

  const onMouseDown = (e: MouseEvent) => {
    // Only respond to left click, skip interactive elements
    if (e.button !== 0 || (e.target as HTMLElement).closest("a, input, textarea, button, select"))
      return;
    e.preventDefault();

    startX = e.clientX;
    startY = e.clientY;
    scrollLeft = viewer.scrollLeft;
    scrollTop = viewer.scrollTop;

    // Create grabbing overlay
    overlay = document.createElement("div");
    overlay.classList.add("grab-to-pan-grabbing");
    document.body.appendChild(overlay);

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    viewer.scrollLeft = scrollLeft - (e.clientX - startX);
    viewer.scrollTop = scrollTop - (e.clientY - startY);
  };

  const onMouseUp = () => {
    if (overlay) {
      document.body.removeChild(overlay);
      overlay = null;
    }
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  viewer.addEventListener("mousedown", onMouseDown);
}

function handleOpenDocumentPropertiesDialog(): void {
  isDocumentPropertiesDialogOpen.value = true;

  outerContainerElement.value?.setAttribute("inert", "");

  if (documentPropertiesDialogElement.value && !documentPropertiesDialogElement.value.open) {
    documentPropertiesDialogElement.value.showModal();
  }
}

function handleCloseDocumentPropertiesDialog(): void {
  isDocumentPropertiesDialogOpen.value = false;

  outerContainerElement.value?.removeAttribute("inert");

  if (documentPropertiesDialogElement.value?.open) {
    documentPropertiesDialogElement.value.close();
  }
}

function formatPdfDate(dateStr: string): string {
  if (!dateStr) return "-";
  const cleaned = dateStr.replace(/^D:/, "");
  const year = parseInt(cleaned.substring(0, 4));
  const month = parseInt(cleaned.substring(4, 6)) - 1;
  const day = parseInt(cleaned.substring(6, 8));
  const hour = parseInt(cleaned.substring(8, 10)) || 0;
  const min = parseInt(cleaned.substring(10, 12)) || 0;
  const sec = parseInt(cleaned.substring(12, 14)) || 0;

  // Parse timezone offset (e.g., +08'00', -05'30', Z)
  const tzMatch = cleaned.substring(14).match(/([Z+\-])(\d{2})?'?(\d{2})?/);
  let date: Date;
  if (tzMatch && tzMatch[1] !== "Z") {
    const sign = tzMatch[1] === "+" ? 1 : -1;
    const tzHour = parseInt(tzMatch[2] || "0");
    const tzMin = parseInt(tzMatch[3] || "0");
    const offsetMs = sign * (tzHour * 60 + tzMin) * 60000;
    date = new Date(Date.UTC(year, month, day, hour, min, sec) - offsetMs);
  } else {
    date = new Date(year, month, day, hour, min, sec);
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function appendTemporaryStyle(cssText: string, styleId: string): HTMLStyleElement {
  const styleElement = document.createElement("style");
  styleElement.dataset.styleId = styleId;
  styleElement.textContent = cssText;
  document.head.appendChild(styleElement);
  return styleElement;
}

function removeTemporaryStyle(styleElement: HTMLStyleElement | null): void {
  styleElement?.remove();
}

const loadPdf = async () => {
  // Create a fresh worker instance per load — Vite's ?worker import bundles
  // the worker without HMR injection and serves it as a blob: URL, which
  // satisfies `worker-src blob:` CSP. A new instance is needed each time
  // because PDF.js terminates the worker when a document is destroyed.
  pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

  const eventBus = new EventBus();
  const linkService = new PDFLinkService({ eventBus });
  const findController = new PDFFindController({
    linkService,
    eventBus,
  });

  const MAX_CANVAS_PIXELS = 2 ** 25;
  const TEXT_LAYER_MODE = 1;
  pdfViewer = new PDFViewer({
    container: document.getElementById("viewerContainer"),
    viewer: document.getElementById("viewer"),
    eventBus,
    linkService,
    findController,
    maxCanvasPixels: MAX_CANVAS_PIXELS,
    textLayerMode: TEXT_LAYER_MODE,
    downloadManager,
  });
  linkService.setViewer(pdfViewer);

  // Expose pdfViewer + eventBus to parent (e.g. PdfViewerCaptureOverlay) so it
  // can wire up capture, page-tracking etc. Must fire before setDocument so the
  // parent can register eventBus listeners before pagesloaded fires.
  emit("open", { pdfViewer, eventBus });

  pdfDocument = await pdfjsLib.getDocument({ url: props.pdf }).promise;
  pdfViewer.setDocument(pdfDocument);
  linkService.setDocument(pdfDocument);
  findController.setDocument(pdfDocument);

  eventBus.on("pagechanging", ({ pageNumber }: { pageNumber: number }) => {
    currentPageNumber.value = pageNumber;
  });

  eventBus.on("pagesinit", async function () {
    const metadata = await pdfDocument?.getMetadata();
    const contentDispositionFilename = metadata?.contentDispositionFilename;
    documentFilename = contentDispositionFilename || pdfjsLib.getPdfFilenameFromUrl(props.pdf);
    document.title = documentFilename;

    const numberOfPages = document.getElementById("numPages");
    if (numberOfPages === null) return;
    numberOfPages.setAttribute("data-l10n-id", "pdfjs-of-pages");
    numberOfPages.setAttribute(
      "data-l10n-args",
      JSON.stringify({ pagesCount: pdfViewer.pagesCount }),
    );
    numberOfPages.textContent = `of ${pdfViewer.pagesCount}`;

    // Populate document properties
    const info = metadata?.info;
    const data = await pdfDocument?.getData();
    const page = await pdfDocument?.getPage(1);
    const viewport = page?.getViewport({ scale: 1 });

    const setText = (id: string, value: string) => {
      const el = document.getElementById(id);
      if (el) el.textContent = value || "-";
    };

    setText("fileNameField", documentFilename);
    if (data) {
      setText(
        "fileSizeField",
        `${(data.length / 1024).toFixed(1)} KB (${data.length.toLocaleString()} bytes)`,
      );
    }
    setText("titleField", info?.Title);
    setText("authorField", info?.Author);
    setText("subjectField", info?.Subject);
    setText("keywordsField", info?.Keywords);
    setText("creationDateField", formatPdfDate(info?.CreationDate));
    setText("modificationDateField", formatPdfDate(info?.ModDate));
    setText("creatorField", info?.Creator);
    setText("producerField", info?.Producer);
    setText("versionField", info?.PDFFormatVersion);
    setText("pageCountField", String(pdfDocument?.numPages));
    if (viewport) {
      const w = ((viewport.width / 72) * 25.4).toFixed(1);
      const h = ((viewport.height / 72) * 25.4).toFixed(1);
      setText("pageSizeField", `${w} × ${h} mm`);
    }
    setText("linearizedField", info?.IsLinearized ? "Yes" : "No");

    // Set default scale to Automatic Zoom
    pdfViewer.currentScaleValue = "auto";
    renderAllThumbnails();
  });

  document.addEventListener("click", (event) => {
    closeSecondaryToolbar(event);
  });
};

watch(currentPageNumber, () => {
  if (currentPageNumber.value === 1) {
    isPreviousPageButtonDisabled.value = true;
  } else {
    isPreviousPageButtonDisabled.value = false;
  }

  if (currentPageNumber.value === pdfViewer.pagesCount) {
    isNextPageButtonDisabled.value = true;
  } else {
    isNextPageButtonDisabled.value = false;
  }

  // Auto-scroll sidebar thumbnail into view
  const container = document.getElementById("thumbnailView");
  if (container) {
    const thumbnails = container.querySelectorAll(".thumbnail");
    thumbnails.forEach((thumb, index) => {
      if (index === currentPageNumber.value - 1) {
        thumb.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
  }
});

onMounted(() => {
  document.documentElement.setAttribute("dir", "ltr");

  pdfJsViewerStyleElement.value = appendTemporaryStyle(pdfJsViewerCss, "pdfjs-viewer-css");
  pdfViewerStyleElement.value = appendTemporaryStyle(pdfViewerCss, "pdf-viewer-css");

  void loadPdf();
});

onBeforeUnmount(() => {
  removeTemporaryStyle(pdfJsViewerStyleElement.value);
  removeTemporaryStyle(pdfViewerStyleElement.value);

  pdfJsViewerStyleElement.value = null;
  pdfViewerStyleElement.value = null;

  document.documentElement.removeAttribute("dir");
});
</script>

<style lang="scss">
#pdf-viewer {
  height: calc((var(--vh, 1vh) * 100));
  background-color: #404040;

  #thumbnailView {
    width: 100%;

    .thumbnailSelectionRing {
      box-sizing: content-box;
    }
  }
}

#viewerContainer {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  position: absolute;
  top: 32px;
  right: 0;
  bottom: 0;
  left: 0;
  outline: none;
}

#sidebarContent.zero-top,
#sidebarContainer.zero-top,
#viewerContainer.zero-top {
  top: 0;
}
</style>
